import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import { useCurrentUser } from "../../auth";
import * as P from "../../constants/permissions";
import { ROLE_GROUP_ADMIN, ROLE_GROUP_USER } from "../../constants/roles";
import {
  Button,
  ConfirmDialog,
  Empty,
  Field,
  Input,
  Loading,
  Status,
} from "../../components/ui";
import { Feedback, LoadError, useAction, useResource } from "./shared";

type Member = {
  id: string;
  sub?: string;
  email?: string;
  displayName?: string;
  roles: string[];
  emailIsVerified?: boolean;
  passwordChangeRequired?: boolean;
};
export default function TeamSettings() {
  const { t } = useTranslation("settings");
  const api = useApi();
  const { permissions, currentUser } = useCurrentUser();
  const can = (p: string) => permissions.includes(p);
  const remote = useResource<Member[]>("/users");
  const action = useAction();
  const [creating, setCreating] = useState(false);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [remove, setRemove] = useState<Member | null>(null);
  const role = (value: string) =>
    t(
      value === "app_admin"
        ? "team.appAdmin"
        : value === ROLE_GROUP_ADMIN
          ? "team.orgAdmin"
          : value === ROLE_GROUP_USER
            ? "team.member"
            : "team.otherRole",
    );
  const create = (event: FormEvent) => {
    event.preventDefault();
    void action.run(async () => {
      await api.post("/users", {
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        email: email.trim(),
      });
      setCreating(false);
      setGivenName("");
      setFamilyName("");
      setEmail("");
      await remote.reload();
    }, t("team.created"));
  };
  const memberAction = (
    member: Member,
    kind: "email/resend" | "password-reset",
  ) =>
    void action.run(
      async () => {
        await api.post(`/users/${member.id}/${kind}`, {});
      },
      t(kind === "email/resend" ? "team.verificationSent" : "team.resetSent", {
        email: member.email,
      }),
    );
  return (
    <section className="settings-section">
      <div className="settings-section-heading">
        <h2>{t("sections.team")}</h2>
        {can(P.ORGANIZATION_CREATE_USER_PERMISSION) && !creating && (
          <Button onClick={() => setCreating(true)}>{t("team.add")}</Button>
        )}
      </div>
      <LoadError {...remote} />
      <Feedback {...action} />
      {creating && (
        <form className="settings-form settings-inline-form" onSubmit={create}>
          <h3>{t("team.add")}</h3>
          <p className="settings-muted">{t("team.createHint")}</p>
          <div className="settings-fields">
            <Field label={t("team.firstName")} htmlFor="member-first">
              <Input
                id="member-first"
                autoComplete="given-name"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
                required
                disabled={action.busy}
                autoFocus
              />
            </Field>
            <Field label={t("team.lastName")} htmlFor="member-last">
              <Input
                id="member-last"
                autoComplete="family-name"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                required
                disabled={action.busy}
              />
            </Field>
            <Field label={t("team.email")} htmlFor="member-email">
              <Input
                id="member-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={action.busy}
              />
            </Field>
          </div>
          <div className="settings-inline">
            <Button
              type="submit"
              disabled={
                action.busy ||
                !givenName.trim() ||
                !familyName.trim() ||
                !email.trim()
              }
            >
              {action.busy ? t("common.saving") : t("team.create")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCreating(false)}
              disabled={action.busy}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      )}
      {remote.loading && !remote.data ? (
        <Loading label={t("common.loading")} />
      ) : remote.data?.length ? (
        <div className="settings-table-wrap">
          <table className="settings-table">
            <caption className="sr-only">{t("sections.team")}</caption>
            <thead>
              <tr>
                <th>{t("team.person")}</th>
                <th>{t("team.role")}</th>
                <th>{t("team.accountState")}</th>
                <th>{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {remote.data.map((member) => (
                <tr key={member.id}>
                  <td data-label={t("team.person")}>
                    <strong>{member.displayName || member.email}</strong>
                    {member.displayName && (
                      <span className="settings-muted settings-block">
                        {member.email}
                      </span>
                    )}
                  </td>
                  <td data-label={t("team.role")}>
                    {member.roles.map(role).join(", ") || t("team.otherRole")}
                  </td>
                  <td data-label={t("team.accountState")}>
                    <Status
                      tone={
                        member.emailIsVerified === false ? "warning" : "neutral"
                      }
                    >
                      {member.emailIsVerified === true
                        ? t("team.emailConfirmed")
                        : member.emailIsVerified === false
                          ? t("team.emailPending")
                          : t("team.stateUnavailable")}
                    </Status>
                    {member.passwordChangeRequired && (
                      <span className="settings-muted settings-block">
                        {t("team.passwordRequired")}
                      </span>
                    )}
                  </td>
                  <td data-label={t("common.actions")}>
                    <div className="settings-row-actions">
                      {member.emailIsVerified === false &&
                        can(
                          P.ORGANIZATION_RESEND_USER_VERIFICATION_EMAIL_PERMISSION,
                        ) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={action.busy}
                            aria-label={t("team.resendFor", {
                              email: member.email,
                            })}
                            onClick={() => memberAction(member, "email/resend")}
                          >
                            {t("team.resend")}
                          </Button>
                        )}
                      {can(
                        P.ORGANIZATION_SEND_USER_PASSWORD_RESET_PERMISSION,
                      ) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={action.busy}
                          aria-label={t("team.resetFor", {
                            email: member.email,
                          })}
                          onClick={() => memberAction(member, "password-reset")}
                        >
                          {t("team.reset")}
                        </Button>
                      )}
                      {can(P.ORGANIZATION_DELETE_USER_PERMISSION) &&
                        member.sub !== currentUser?.sub && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={action.busy}
                            aria-label={t("team.removeFor", {
                              email: member.email,
                            })}
                            onClick={() => setRemove(member)}
                          >
                            {t("common.remove")}
                          </Button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !remote.error && (
          <Empty title={t("team.empty")} description={t("team.emptyHint")} />
        )
      )}
      <ConfirmDialog
        open={Boolean(remove)}
        onOpenChange={(open) => !open && setRemove(null)}
        title={t("team.removeTitle")}
        description={t("team.removeDescription", {
          person: remove?.displayName || remove?.email,
        })}
        confirmLabel={t("team.remove")}
        cancelLabel={t("common.cancel")}
        pending={action.busy}
        onConfirm={async () => {
          if (!remove) return;
          await action.run(async () => {
            await api.del(`/users/${remove.id}`);
            setRemove(null);
            await remote.reload();
          }, t("team.removed"));
        }}
      />
    </section>
  );
}
